import React from 'react';
import { Dropdown as PrimeDropdown } from 'primereact/dropdown';
import { FloatLabel } from 'primereact/floatlabel';

// PrimeReact's Dropdown type doesn't declare every prop this wrapper forwards.
// Treat it as permissive so valid runtime props pass.
const Dropdown = PrimeDropdown as unknown as React.ComponentType<any>;

export interface SelectOption {
  value: string;
  label: string;
  /** Optional trailing pill (e.g. an owning project) shown after the label. */
  tag?: string;
  /** Visual tone for the tag pill. 'neutral' = muted grey, 'accent' = coloured. */
  tagTone?: 'neutral' | 'accent';
  /** Optional muted second line under the label (e.g. a human description). */
  description?: string;
  /** Optional leading icon class names (e.g. PrimeIcons 'pi pi-envelope'). */
  icons?: string[];
}

const tagClasses = (tone?: 'neutral' | 'accent') =>
  tone === 'accent'
    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300';

const Pill = ({ option }: { option: SelectOption }) =>
  option.tag ? (
    <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${tagClasses(option.tagTone)}`}>
      {option.tag}
    </span>
  ) : null;

// The compact single line shown in the closed field for the SELECTED value.
const renderValueContent = (option: SelectOption) => (
  <span className="flex items-center gap-2 w-full min-w-0">
    <span className="truncate">{option.label}</span>
    <Pill option={option} />
  </span>
);

// The richer row shown in the open PANEL: leading channel icons, a bold label
// with an optional muted description beneath, and a trailing pill. `min-w-0` +
// `truncate` keep long labels inside the panel instead of forcing a horizontal
// scrollbar.
const renderItemContent = (option: SelectOption) => (
  <span className="flex items-center gap-2 w-full min-w-0">
    {option.icons?.length ? (
      <span className="flex items-center gap-1 shrink-0 text-gray-400 dark:text-gray-500">
        {option.icons.map((ic, i) => <i key={i} className={`${ic} text-xs`} />)}
      </span>
    ) : null}
    <span className="flex flex-col min-w-0 flex-1">
      <span className="truncate font-medium">{option.label}</span>
      {option.description ? (
        <span className="truncate text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
      ) : null}
    </span>
    <Pill option={option} />
  </span>
);

export interface FloatLabelDropdownProps {
  name: string;
  label: string;
  value: string | null;
  onChange: (e: { target: { name: string; value: string } }) => void;
  options: SelectOption[];
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  showClear?: boolean;
  filter?: boolean;
  filterPlaceholder?: string;
  loading?: boolean;
  onFilter?: (e: { filter: string }) => void;
  emptyFilterMessage?: string;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  /** Fires as the option panel scrolls, for infinite-scroll loading. Delivered
   *  through the virtual scroller: the Dropdown itself has no such prop, and
   *  passing one to it only reached the DOM as an unknown handler. */
  onPanelScroll?: (e: React.UIEvent<HTMLElement>) => void;
  appendTo?: 'self' | HTMLElement | null;
  filterInputAutoFocus?: boolean;
  optionDisabled?: string | ((option: SelectOption) => boolean);
  /** Enable PrimeReact virtual scrolling for large option lists, e.g. { itemSize: 40 }. */
  virtualScrollerOptions?: { itemSize: number };
  /** Size the open panel to the field's width (and stop options overflowing into a
   *  horizontal scrollbar). Opt-in so plain selects keep the default content sizing. */
  matchTriggerWidth?: boolean;
}

export const FloatLabelDropdown = React.forwardRef<HTMLDivElement, FloatLabelDropdownProps>(
  ({ name, label, value, onChange, options, error, helperText, disabled, required, placeholder, showClear, filter, filterPlaceholder, loading, onFilter, emptyFilterMessage, className, style, testId, onPanelScroll, appendTo, filterInputAutoFocus, optionDisabled, virtualScrollerOptions, matchTriggerWidth }, ref) => {
    // When asked to match the field width, measure the wrapper (which is `w-full`,
    // i.e. the field's width) and pin the panel to it. This is what stops long
    // options widening the panel past the field and spilling into a horizontal
    // scrollbar. Kept off by default so plain selects size to their content.
    const wrapRef = React.useRef<HTMLDivElement | null>(null);
    const [panelWidth, setPanelWidth] = React.useState<number | undefined>(undefined);
    React.useEffect(() => {
      const el = wrapRef.current;
      if (!matchTriggerWidth || !el || typeof ResizeObserver === 'undefined') return;
      const update = () => setPanelWidth(el.getBoundingClientRect().width);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, [matchTriggerWidth]);
    const setRefs = (node: HTMLDivElement | null) => {
      wrapRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    };
    return (
      <div ref={setRefs} className={className} style={style}>
        <FloatLabel>
          <Dropdown
            id={name}
            name={name}
            // Coerce an empty value to null: PrimeReact treats '' as "has a
            // value" and would show the clear (✕) icon before anything is
            // picked. null keeps the clear icon hidden until a real selection.
            value={value || null}
            onChange={(e: any) => onChange({ target: { name, value: e.value } })}
            options={options}
            optionLabel="label"
            optionValue="value"
            optionDisabled={optionDisabled}
            itemTemplate={(option: SelectOption) => renderItemContent(option)}
            valueTemplate={(option: SelectOption, props: any) =>
              option ? renderValueContent(option) : <span>{props.placeholder}</span>
            }
            placeholder={placeholder}
            disabled={disabled}
            showClear={showClear}
            filter={filter}
            filterBy="label,tag"
            filterPlaceholder={filterPlaceholder}
            filterInputAutoFocus={filterInputAutoFocus}
            onFilter={onFilter ? (e: any) => onFilter({ filter: e.filter }) : undefined}
            emptyFilterMessage={emptyFilterMessage}
            loading={loading}
            appendTo={appendTo}
            panelClassName={matchTriggerWidth ? 'sui-dropdown-panel' : undefined}
            panelStyle={matchTriggerWidth && panelWidth ? { width: panelWidth, maxWidth: 'none' } : undefined}
            virtualScrollerOptions={
              onPanelScroll
                ? { itemSize: 40, ...(virtualScrollerOptions || {}), onScroll: onPanelScroll }
                : virtualScrollerOptions
            }
            className={`w-full ${error ? 'p-invalid' : ''}`}
            data-testid={testId}
          />
          <label htmlFor={name}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </FloatLabel>
        {helperText && <small className={error ? 'p-error' : 'text-gray-500'}>{helperText}</small>}
      </div>
    );
  }
);

FloatLabelDropdown.displayName = 'FloatLabelDropdown';
export default FloatLabelDropdown;
